import { DeepPartial } from 'react-hook-form';

import {
  PaymentMethod,
  SUPPORTED_COUNTRY,
  SUPPORTED_LANGUAGE,
  MaxAgeExpiration,
} from 'utils/constants';
import {
  Cta,
  Environment,
  FeatureFlags,
  OrderAddress,
  SiteConfig as BackendSiteConfig,
} from '__generated__/graphql';
import { FieldPropsConfig, FormFieldConfig } from 'ui/forms/custom/types';
import { OrderSearchOperationName } from 'ui/forms/OrderLookUp/types';
import { Resources } from 'locales/localeType';

type PaymentsUnion = `${PaymentMethod}`;

export type FormConfig = Array<Array<FormFieldConfig>>;
export type Forms = {
  registration: FormConfig;
  registrationV2: FormConfig;
  accountAddress?: FormConfig;
  createAccountInConfirmation?: FormConfig;
  checkoutAddress?: FormConfig;
  checkoutAmazonPayBillingAddress?: FormConfig;
  checkoutContactInfo?: FormConfig;
  editProfile?: FormConfig;
  quickRegistration?: FormConfig;
  checkoutAddressGuest?: FormConfig;
  addPaymentMethod?: FormConfig;
  orderLookUpReturns: FormConfig;
  changePassword?: FormConfig;
  checkoutContactInfoNoSubscription?: FormConfig;
  resetPassword?: FormConfig;
  editPhone?: FormConfig;
};
export type FormTransformers = {
  [key in keyof Forms]?: (args: any) => unknown;
};

// used on client side
export type Env = Environment & DeepPartial<NodeJS.ProcessEnv>;

export type PublicEnvironmentConfig = SiteConfig & {
  env: Env;
  features: FeatureFlags;
};

export type PriceFormatterConfig = {
  currencyDisplay?: 'code' | 'narrowSymbol' | 'symbol' | 'name';
  showPricesWithoutTrailingZeroes?: boolean;
  compactDisplay?: 'short' | 'long';
  signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
};

export type ISavedCreditCardElementType = 'saved' | 'new';

export enum ComponentType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
}
export type IComponentTypes =
  (typeof ComponentType)[keyof typeof ComponentType];

export type SiteConfig = BackendSiteConfig & {
  applePaySupportedNetworks: string[]; //required in the node module so a value is needed everywhere
  flag: string;
  region: string;
  previewContentLanguage?: string;
  countryCode: Lowercase<SUPPORTED_COUNTRY>;
  countryName: string;
  supportedLocales: string[];
  /** @see currency must be kept in sync with {@link https://github.com/cyber-cats/puma-gql/tree/main/src/backend-sites} */
  currency: {
    code: string;
    symbol: string;
    decimalsToDisplay: number; // This one is only relevant to the front-end
  };
  /** These values must be kept in sync with {@link https://github.com/cyber-cats/puma-gql/tree/main/src/backend-sites} */

  pdpLowInventoryThreshold?: number;
  addressSearch: {
    shipping: string[];
    billing: string[];
  };
  addressCountries: { label: string; value: SUPPORTED_COUNTRY }[];
  addressStates: {
    [key in SUPPORTED_COUNTRY]?: { name: string; value: string | number }[];
  };
  liveChat?: {
    provider: 'radial' | 'zendesk' | 'freshChat' | 'ada';
    script?: string;
    code?: string;
    /**
     * Represents the opening hours of a business or service.
     * @property {number[]} hours - An array of hours (0-23) representing when the business is open.
     * @property {number[]} days - An array of days of the week (0 = Sunday, 6 = Saturday) representing which days the business is open.
     */
    openingHours?: {
      hours: number[];
      days: number[];
    };
    renderInMainNav?: boolean;
  };
  priceSliderInterval?: number;
  priceInputMaxLength: number;
  phoneAnchorNumberInCheckout?: string;
  phoneTextNumberInCheckout?: string;
  cashOnDeliveryFee?: string;
  regionCode: string;
  validation: {
    address: {
      line1: string;
      line2?: string;
      title?: string;
      length?: {
        min: number;
        max: number;
      };
      amazonAddressMaxLength?: number;
    };
    name: {
      length: {
        min: number;
        max: number;
      };
      regex?: string;
    };
    companyName?: {
      length: {
        min: number;
        max: number;
      };
    };
    password?: string;
    phone: {
      length: {
        min: number;
        max: number;
      };
      regex: string;
      prefix?: string;
    };
    email?: string;
    postalCode: {
      [key in SUPPORTED_COUNTRY]?: string;
    };
    postalCodeMaxLength?: number;
    city?: string;
    orderNo?: string;
    holderName?: {
      regex?: string;
    };
  };
  forms: { [key in keyof Forms]: Forms[key] };
  fieldProps: Record<string, FieldPropsConfig>;
  /**
   * This determines what payment methods to show in the payment method form.
   * Order matters (it will determine how they render).
   */
  selectablePaymentMethods: SelectablePaymentMethodsType[];
  paymentMethodsMixedWithGiftCard?: PaymentMethod[];
  defaultPaymentMethod?: SelectablePaymentMethodsType;
  paymentCreditCardErrors?: { [key: string]: string };
  deliveryTime?: {
    deliveryTimeCode: string;
    deliveryTimeDisplayText: string;
  }[];
  nonCustomFormConfiguration?: {
    [key: string]: { name: string; childOrder?: string[] }[];
  };
  addressFormat?: Array<Array<keyof OrderAddress>>;
  addressFormatOneLiner?: string;
  addressFormatOneLinerWithAddress2?: string;
  myAccountDashboardConfiguration?: Array<{
    name: string;
    childOrder: string[];
  }>;
  cashOnDeliveryMaxValue?: number;
  dateFormatWithWeekDay?: string[];
  scheduledArrivalDateFormat?: string[];
  colors?: {
    [key: string]: string;
  };
  // When coming from a Google shopping link, show simplified PDP page
  simplifiedGoogleShoppingPdpEnabled?: boolean;
  staticFeatures: {
    showAddAddressButtonInAccountDashboard: boolean; // Add a new address in My Account - enabled for JP
    productFilterCollapsible: boolean; // Shows collapsible filter in the PLP - enabled for JP
    showTitleTranslationInAccountOrderHistory?: boolean; // Enables the collapsible filter in the PLP - enabled for JP
    canRemoveWishlistItemsFromPDP: boolean; // Allows to remove the product from the wishlist in the PDP - enabled for AU,DE,GB,IN,JP,NZ
    showPhoneInCheckout?: boolean; // Show phone icon in checkout - enabled for US,CA
    showNewsletterSignupOnOrderConfirmation?: boolean; // Show newsletter in order confirmation - enabled for JP
    showProductStoryKeywordTitle?: boolean; // Display the title of origin of the product in the PDP - enabled for JP
    pdpShowStockMessageBySFCCLogic?: boolean; // Display the stock message coming from SFCC in the PDP - enabled for JP
    useStockMessageFromResponse?: boolean; // Display the stock message from response - enabled for JP
    enableStockStatusInPDP?: boolean; // Display the stock status of the product in the PDP - enabled for JP
    showShippingDiscountInCheckout?: boolean; // Display the shipping discount in the checkout - enabled for JP
    showMetricAsDefault?: boolean; // Display default metrics in the size guide in the PDP - enabled for JP
    showBreadcrumbsOnOrderDetails?: boolean; // Display breadcrumbs in the order detail in My Account  - enabled for JP
    showSearchResult?: boolean; // Display the search result in the PLP - enabled for JP but its not used
    // Temporary solution until SANI-1186 release
    hideNewsletterSignupButtonOnMobile?: boolean; // Hide newsletter subcription in mobile in footer - enalebled for JP
    hideCalloutMessage?: boolean; // Hide callout message in GWP - enabled for JP
    hideEanColorCode?: boolean; // Hide ean atributte color code in old GWP - enabled for JP
    redirectToAccountDashboardAfterProfileUpdate?: boolean; // Redirect to My Account after update your profile - enabled for JP
    pdpDisableReturns?: boolean; // Display return message in old GWP - enabled for JP
    showCookieConsentLayer?: boolean; // Display cookies consent - enabled for all regions
    checkoutHideShippingAndReturnsMessage?: boolean; // Hide shipping and returns message in the checkout - enabled for JP
    allowGuestAccessToOrderDetails: boolean; // Allow the guest access to the order details - enabled for AU,DE,GB,IN,JP,NZ
    showSaleBadgeOnProductImageEnabled?: boolean; // Display the sales badge text in the product image - enabled for AU,IN,JP,NZ
    showPricesWithoutTrailingZeroes?: boolean; // Currently dont used
    reviewOrderOnCheckout?: boolean; // Display order step in checkout - enabled for JP
    showOrderTrackingNumberInOrderDetails?: boolean; // Display order tracking number in MyAccount order details - enabled for AU,DE,GB,NZ
    parcelLabTrackingEnabled?: boolean; // Tracking number parcellab - enabled for AU,DE,GB,NZ
    checkoutShowSaveCardCheckbox?: boolean; // Display checkbox to save card at checkout - enabled for JP
    showPriceDifference?: boolean; //Shows the price difference in the edit product modal in ShoppingCart and Wishlist - enabled for AU,CA,DE,GB,IN,NZ,US
    useSFCCDividerForSEODescription?: boolean; // Display a divider in read more for SEO - enabled for JP
    showOrderSummaryAtOrderHistory?: boolean; // Show order summary at order history in order details - enabled for AU,DE,GB,NZ
    showRefundInformationAtOrder?: boolean; // Display refund information in order details - enabled for JP
    personalizationEnabled?: boolean; // Enable product customization - enabled  for AU,DE,IN,NZ,US
    hideSoldOutColorsInStylesSelection?: boolean; // Hide sold out color in styles selection product - enabled for AU,CA,DE,GB,IN,JP,NZ,US
    editPasswordResultErrorDisabled?: boolean; // Show message error in custom edit password form - enabled for GB,JP
    showAllPricesAtOnce?: boolean; // Display all prices at once - enabled for JP
    displayPromoDiscountBreakdown?: boolean; // Display promo discount title in cart - enabled for JP
    hideTermsOnLoginForm?: boolean; // Hide terms and conditions on login with email and password - enabled for JP
    displayPromoAppliedPromotion?: boolean; // Display promotion in Product cart - enabled for JP
    showOneFinalPriceOnPurchasedProduct?: boolean; // Display final price in order detail - enabled for JP
    setAutoCapitalizeOff?: boolean; // no autocapitalize in credit card form - enabled for JP
    hideMyAccountSubtitle?: boolean; // Hide subtitle in My account - enabled for JP
    showWishlistIconBadgeOnDropdownMenu?: boolean; // Display wishlist icon badge in menu - enabled for JP
    hidePaymentInformationOnAccountPage?: boolean; // Hide payment information in order payments details - enabled for JP
    allowEditIncorrectSavedAddressCheckout?: boolean; // Allow edit incorrect saved address in checkout - enabled for JP
    hideBulletsListsPdp?: boolean; // Hide bullets PLP in product history - enabled for JP
    showKlarnaDisclaimerMessage?: boolean; // Show klarna disclaimer message - enabled for US
    showSearchFormOnError?: boolean; // Display search form in case of error - enabled for JP
    showProductRecommenderOnError?: boolean; // Display product recommender in case of error - enabled for JP
    showSearchTipsOnError?: boolean; // Display search tips in no search results - enabled for JP
    authorityToLeaveEnabled?: boolean; // Authorization to leave the delivery at a location - enabled for AU,NZ
    // Temporary solution while gradually rolling out the payment form refactor.
    // Once all regions are using the new one, this setting will be redundant.
    useNewPaymentForm?: boolean; // Use the payment form in checkout - enabled for AU,CA,DE,GB,IN,NZ,US
    useVerticalMedia?: boolean; // Display vertical product image - enabled for GB
    delayForterLoad?: boolean; // Delay the forter load - enabled for GB
    useProductSizeChart?: boolean; // Display the product sizing chart in PDP - enabled for IN,JP
    useDefaultJapanChart?: boolean; // Display the default JP chart in PDP - enabled for JP
    useAccountNotificationFormV2?: boolean; // Currently dont used
    quickAddressFinder?: boolean; // Use quick address finder in checkout - enabled for CA,US
    ukAddressForm?: boolean; // Display unique text for the UK form in checkout - enabled for GB
    useStockInfoRedesign?: boolean; // Display SFCC stock message - enabled for JP
    hidePickUpWhereYouLetOff?: boolean; // Hide PickUpWhereYouLetOff component - enabled for GB,IN
    isPNARegion?: boolean; // Verify that it is the PNA site - enabled for CA,US
    mixedCart?: boolean; // Mixed cart to deliver on different dates - enabled for JP
    hideEmailNotifications?: boolean; // Hide email notifications in account settings - enabled for GB,IN
    //Temporary override New Zealand setting phoneInProfileEnabled
    phoneInProfileEnabled?: boolean; // Edit the phone in myAccount - enabled for IN
    translateToJapanese?: boolean; // Currently dont used
    enablePopularity?: boolean; // Display popularity message in minicart - enabled for CA,US
    showPromoExclusionMsgAboveProductDetails?: boolean; // Display promoExclusion message of the product in shopping cart - enabled for all regions
    showPromoExclusionMsgInBaseSize?: boolean; // Display promoExclusion message of the product size in shopping cart - enabled for all regions
    popoverLoginEnabled?: boolean; // Display popover login - enabled for AU,CA,GB,NZ,US
    multipleModelMeasurementEnabled?: boolean; // Display multiple model measurement in product variant selector - enabled for CA,GB,US
    removeReturnIframe?: boolean; // Return order from my orders - enabled for CA,US,GB
    showGiftWithPurchaseModalInMobile?: boolean; // Display GWP in mobile - enabled for JP
    giftWithPurchaseNewDesign?: boolean; // Use the new GWP design - enabled for US,CA
    searchLocalImagesOnProductComparison?: boolean; // Search for local images in the product comparison component - enabled for IN,JP
    searchLocalImagesOnDropCalendar?: boolean; // Search for local images in the drop calendar - enabled for JP
    styleBazaarVoiceModal?: boolean; // BazaarVoice style in the modal reviews - enabled for JP
    showFreeShippingProgressBar?: boolean; // Show shipping progress bar to get to free shipping - enabled for US,CA
    useVariantPromotionExclusion?: boolean; // Display variant promotion exclusion message - enabled for JP
    showPromoExclusionMsgInGray?: boolean; // Display promotion exclusion message in gray - enabled for JP
    showPinterestFooterIcon?: boolean; // Show Pinterest icon in the footer - enabled for all regions
    showPumaCard?: boolean; // Show puma card in overview - enabled for JP
    returnsPageDisabled?: boolean; // Disable return page - enabled for JP
    hideWeightInProductMeasurements?: boolean; // Hide weight in product measurements - enabled for IN
    ambassadorCardsEnabled?: boolean; // Enable the ambassador card section in my account - enabled for JP,US
    addAddressResultErrorDisabled?: boolean; // Show error when adding a new address - enabled for AU,DE,GB,JP,NZ
    triggerCartTotalEvent?: boolean; // Trigger cart total event GA4 - enabled for CA,US
    isPriceFilterSliderEnabled?: boolean; // Validate if the price sliding filter is enabled - enabled for all regions
    showHeaderSubHeaderProductDetail?: boolean; // Display the subHeader product details - enabled for CA,US
    showFinancialIncentivesLink?: boolean; // Show the financial incentives link in the newsletter signUp form - enabled for US
    showPaymentPartnersInFooter: boolean; // Show Payment partnets in the footer - enabled for AU,DE,GB,IN,JP,NZ
    showCookieBanner?: boolean; // Show cookie banner - enabled for AU,CA,DE,IN,NZ,US
    returnsOnOrderDetailsPage: boolean; // Return the order from the order details - enabled for AU,DE,GB,IN,JP,NZ
    singInInConfirmationForm: boolean; // Sing in the confirmation form - enabled for AU,DE,GB,JP,NZ
    enableModelMeasurements?: boolean; // Currently dont used
    showModifiedResetPassFormPopup: boolean; // Show reset password popup in authenticate forgot password form - enabled for JP
    showTimeZoneName?: boolean; // Show time zone name message - enabled for AU,CA,GB,NZ,US
    shapeSecurity?: boolean; // Inject mecrury script - enabled for CA,US
    enableRrdTracking?: boolean; // Currently dont used
    showFreeShippingCost?: boolean; // Show free shipping cost message - enabled for CA,JP,US
    popoverLoginEnabledJP?: boolean; // Enable the Popover login - enabled for JP
    enableItemLinksInCartSummary?: boolean; // Enable item links in cart summary - enable for IN
    registerOTPVerificationEnabled?: boolean;
    useMasterIdsInCartRecommenders?: boolean;
    isGmoEnabled?: boolean;
    isSalesfloorEnabled?: boolean;
    otpLoginEnabled?: boolean;
    useDataguard?: boolean; // Enable Dataguard
  };
  shoppingCartItemType?: IComponentTypes;
  miniCartItemType?: IComponentTypes;
  savedCreditCardTemplate: ISavedCreditCardElementType[];
  pdpVideosConfig: {
    enabled: boolean;
    productVideoOptions?: {
      controls: boolean;
      autoPlay: boolean;
      loop: boolean;
      muted: boolean;
    };
    posterSecond?: number;
    numberOfLoops?: number;
    videoPosition?: number;
  };
  orderedPaymentPartners: PAYMENT_PARTNERS_UNION[];
  orderedNewsletterSignupFormFields?: Record<string, Array<string>>;
  // anti-bot JS script ID
  pxCloudID?: string;
  pixleeId?: { [key in SUPPORTED_LANGUAGE]?: string };
  // TODO: Refactoring ticket: https://pumaglobal.atlassian.net/browse/CBC-7472
  newsletterProvider?: string;
  insiderIntegration?: {
    partnerId: string;
    partnerName: string;
  };
  plpShowDefaultSortOption?: boolean;
  newsletter?: {
    contactSources: {
      checkOut: string;
      signUp: string;
      footer?: string;
    };
    fields?: {
      gender?: { values: string[]; required?: boolean };
    };
  };
  socialMediaLinks?: {
    instagram?: string;
    youtube?: string;
    pinterest?: string;
    twitter?: string;
    facebook?: string;
  };
  searchSymbolsMinValue: number;
  charactersNeededToSubmitSearch: number;
  orderLookUpQuery: OrderSearchOperationName;
  searchEngineIndexableLanguages: SUPPORTED_LANGUAGE[];
  cookieConsent?: {
    provider: COOKIE_CONSENT_PROVIDER;
    documentLanguage?: boolean;
  };
  additionalCtaClass?: string;
  pumaAppDownloadUrl?: string;

  analytics: {
    releaseVersion: string;
  };
  reviewFormModalSupportLinks?: SupportLink[];
  badgeIdsExcludedBySaleStatus: string[];
  bloomreach?: {
    viewId: string;
    // For some sites, we need to load two separate Bloomreach pixel scripts, see https://pumaglobal.atlassian.net/browse/GS-9027.
    // Because we’re loading two scripts, Bloomreach require us to set the data as two separate keys on window.
    // objectName is for the primary script, seoObjectName is the optional second script that some sites may have.
    objectName: string;
    seoObjectName?: string;
  };
  navCategoryMaxColumnCount?: number;
  stateFieldLabels?: {
    CA?: {
      label: string;
      placeholder: string;
    };
  };
  branchAnalyticsData?: {
    agencyId: string;
  };
  resetSearchConditionsOptions?: {
    showResetSearchConditionsButton: boolean;
    isRouterScrollEnabled: boolean;
  };
  /** @see enableEstimatedDeliverDatesByItem must be kept in sync with {@link https://github.com/cyber-cats/puma-gql/tree/main/src/backend-sites} */
  enableEstimatedDeliverDatesByItem?: boolean;
  amazonWidget?: {
    countryCode: 'na' | 'fe' | 'eu'; // us: 'na', jp: 'fe', others: 'eu'
    widgetSource: 'js' | 'lpa/js'; // us: 'js', others: 'lpa/js'
  };
  amazonPayButtonOptions?: {
    color: string;
    size: string;
  };
  maxCountRecordKeeper: number;
  absoluteLinkBreadcrumb?: boolean;
  amazonPayScriptUrl?: string;
  emarsysMerchantId?: string; // Emarsys Web Extend: CA and US
  legal?: {
    termsAndConditions: string;
    privacyPolicy: string;
  };
  maxAgeCookiesExpires?:
    | MaxAgeExpiration.DAILY
    | MaxAgeExpiration.WEEKLY
    | MaxAgeExpiration.MONTHLY;
  maximumApplicableCards: number;
};

export type COOKIE_CONSENT_PROVIDER = 'Google' | 'OneTrust';

type SupportLinkPopup = {
  popup: boolean;
  newtab?: never;
};
type SupportLinkNewtab = {
  newtab: boolean;
  popup?: never;
};

type SupportLinkOpenWith = Partial<SupportLinkPopup | SupportLinkNewtab>;

export type SupportLink = Required<Pick<Cta, 'id' | 'link'>> & {
  title: keyof Resources;
} & SupportLinkOpenWith;

export type SelectablePaymentMethodsType = Exclude<
  PaymentsUnion,
  | typeof PaymentMethod.GIFT_CARD
  | typeof PaymentMethod.PAYPAL
  | typeof PaymentMethod.APPLE_PAY
  | typeof PaymentMethod.GOOGLE_PAY
  | typeof PaymentMethod.KLARNA_PAYMENTS
>;

export type PAYMENT_PARTNERS_UNION =
  | 'amazonPay'
  | 'master'
  | 'visa'
  | 'payPay'
  | 'JCB'
  | 'americanExpress'
  | 'diners'
  | 'applePay'
  | 'googlePay'
  | 'payPal'
  | 'klarna'
  | 'zip'
  | 'maestro'
  | 'afterPay'
  | 'discover'
  | 'weChatPay'
  | 'alipay'
  | 'UPI'
  | 'rupay';
